/**
 * @fileoverview Editing plan modal for final review and configuration of the editing plan
 */

import React, { useState } from 'react';
import { CloseOutlined, ReloadOutlined, PlayCircleOutlined } from '@ant-design/icons';
import TrebbleModal from '../../common/TrebbleModal.tsx';
import TrebbleButton from '../../common/TrebbleButton.tsx';
import TrebbleSwitch from '../../common/TrebbleSwitch.tsx';
import TrebbleInput from '../../common/TrebbleInput.tsx';
import TrebbleRadio from '../../common/TrebbleRadio.tsx';
import TrebbleCard from '../../common/TrebbleCard.tsx';
import TrebbleBadge from '../../common/TrebbleBadge.tsx';
import { TrebbleTitle, TrebbleText, TrebbleParagraph } from '../../common/TrebbleTypography.tsx';
import TrebbleSpace from '../../common/TrebbleSpace.tsx';
import Utils from '../../../models/helper/Utils';
import { CONFIDENCE_THRESHOLD_UNCERTAIN, CONFIDENCE_THRESHOLD_MEDIUM, ALWAYS_SELECT_CORE_CHIPS } from '../constants/schema.js';
// @ts-ignore
import ti18n from 'i18n!nls/Deepcuti18n';

const { Title, Text, Paragraph } = { Title: TrebbleTitle, Text: TrebbleText, Paragraph: TrebbleParagraph };

/**
 * @typedef {import('../types/deepcut.js').AnalysisResults} AnalysisResults
 * @typedef {import('../types/deepcut.js').UserFeedback} UserFeedback
 * @typedef {import('../types/deepcut.js').ChipUpdate} ChipUpdate
 * @typedef {import('../types/deepcut.js').QuestionAnswer} QuestionAnswer
 */

/**
 * Editing plan modal for final review and configuration
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the modal is open
 * @param {AnalysisResults} props.plan - Analysis results with the editing plan
 * @param {Function} props.onClose - Callback for closing the modal
 * @param {Function} props.onUpdatePlan - Callback for updating the plan
 * @param {Function} props.onStartEditing - Callback for starting the editing process
 * @param {boolean} props.isUpdating - Whether the plan is being updated
 * @returns {React.ReactElement}
 */
export default function EditingPlanModal({
  open,
  plan,
  onClose,
  onUpdatePlan,
  onStartEditing,
  isUpdating
}) {
  const [chipStates, setChipStates] = useState(() => {
    const initial = {};
    
    plan.plan.forEach(chip => {
      // Check if we should always select core chips
      if (ALWAYS_SELECT_CORE_CHIPS && Utils.getInstance().getPreselectedDeepCutChips().includes(chip.chipLabel)) {
        initial[chip.chipLabel] = true;
      } else {
        // Since all chips are for removal, default to selected (they represent content to remove)
        initial[chip.chipLabel] = true;
      }
    });
    return initial;
  });

  const [questionAnswers, setQuestionAnswers] = useState({});
  const [additionalDirectives, setAdditionalDirectives] = useState("");

  const handleChipToggle = (chipLabel, checked) => {
    setChipStates(prev => ({
      ...prev,
      [chipLabel]: checked
    }));
  };

  const handleQuestionAnswer = (questionId, answer) => {
    setQuestionAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const handleUpdatePlan = () => {
    const chipUpdates = plan.plan
      .filter(chip => chipStates[chip.chipLabel]) // Only include chips that are toggled ON (selected for removal)
      .map(chip => ({
        chipLabel: chip.chipLabel,
        displayLabel: chip.displayLabel,
        action: 'remove',
      }));

    const answers = plan.probingQuestions.map(question => ({
      questionId: question.id,
      answer: questionAnswers[question.id] || "",
    }));

    const feedback = {
      chipUpdates,
      questionAnswers: answers,
      newDirectives: additionalDirectives ? [additionalDirectives] : [],
    };

    onUpdatePlan(feedback);
  };

  const getBadgeColor = (hits) => {
    if (hits > 50) return "red";
    if (hits > 20) return "orange";
    return "blue";
  };

  return (
    <TrebbleModal
      title={
        <div>
          <Title level={3} style={{ margin: 0 }}>{window.getI18n(ti18n, 'EDITING_PLAN_REVIEW')}</Title>
          <Text type="secondary">
            {window.getI18n(ti18n, 'CONFIGURE_EDITING_PREFERENCES')}
          </Text>
        </div>
      }
      open={open}
      onCancel={onClose}
      width={1000}
      bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
      footer={
        <TrebbleSpace>
          <TrebbleButton icon={<CloseOutlined style={{ color: 'var(--trebble-primary)' }} />} onClick={onClose}>
            {window.getI18n(ti18n, 'CANCEL')}
          </TrebbleButton>
          <TrebbleButton 
            icon={<ReloadOutlined style={{ color: 'var(--trebble-primary)' }} />}
            onClick={handleUpdatePlan}
            loading={isUpdating}
          >
            {isUpdating ? window.getI18n(ti18n, 'UPDATING') : window.getI18n(ti18n, 'UPDATE_PLAN')}
          </TrebbleButton>
          <TrebbleButton 
            type="primary"
            icon={<PlayCircleOutlined style={{ color: 'white' }} />}
            onClick={onStartEditing}
          >
            {window.getI18n(ti18n, 'START_EDITING')}
          </TrebbleButton>
        </TrebbleSpace>
      }
    >
      <TrebbleSpace direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Suggested Intents */}
        {plan.suggestedIntents.length > 0 && (
          <div>
            <Title level={4}>{window.getI18n(ti18n, 'SUGGESTED_INTENTS')}</Title>
            <TrebbleSpace direction="vertical" size="middle" style={{ width: '100%' }}>
              {plan.suggestedIntents.map((intent) => {
                const isSelected = intent.code === plan.selectedIntent || intent.label === plan.selectedIntent;
                return (
                  <TrebbleCard 
                    key={intent.code}
                    style={{
                      borderColor: isSelected ? 'var(--trebble-primary)' : undefined,
                      backgroundColor: isSelected ? 'var(--trebble-primary-100)' : undefined
                    }}
                  >
                    <TrebbleSpace direction="vertical" size="small" style={{ width: '100%' }}>
                      <TrebbleSpace>
                        <Text strong style={{ color: isSelected ? 'var(--trebble-primary)' : undefined }}>
                          {intent.label}
                        </Text>
                        {isSelected && (
                          <TrebbleBadge count={window.getI18n(ti18n, 'SELECTED')} style={{ backgroundColor: 'var(--trebble-primary)' }} />
                        )}
                        <TrebbleBadge 
                          count={`${Math.round(intent.confidence * 100)}% ${window.getI18n(ti18n, 'CONFIDENCE')}`}
                          style={{ 
                            backgroundColor: intent.confidence >= CONFIDENCE_THRESHOLD_UNCERTAIN ? 'var(--trebble-primary)' : 
                                           intent.confidence >= CONFIDENCE_THRESHOLD_MEDIUM ? 'orange' : 
                                           'var(--color-black-20)'
                          }}
                        />
                      </TrebbleSpace>
                      <Text type="secondary">
                        {intent.rationale}
                      </Text>
                    </TrebbleSpace>
                  </TrebbleCard>
                );
              })}
            </TrebbleSpace>
          </div>
        )}

        {/* Strategy Summary */}
        <div>
          <Title level={4}>{window.getI18n(ti18n, 'STRATEGY_SUMMARY')}</Title>
          <TrebbleCard style={{ backgroundColor: '#f0f8ff' }}>
            <Paragraph style={{ whiteSpace: 'pre-line', margin: 0 }}>
              {plan.strategySummary}
            </Paragraph>
          </TrebbleCard>
        </div>

        {/* Editing Plan */}
        <div>
          <Title level={4}>{window.getI18n(ti18n, 'EDITING_PLAN_TITLE')}</Title>
          <TrebbleSpace direction="vertical" size="middle" style={{ width: '100%' }}>
            {plan.plan.map((chip) => (
              <TrebbleCard key={chip.chipLabel}>
                <TrebbleSpace style={{ width: '100%', justifyContent: 'space-between' }}>
                  <div style={{ flex: 1 }}>
                    <TrebbleSpace direction="vertical" size="small">
                      <TrebbleSpace>
                        <Text strong>{chip.displayLabel}</Text>
                        <TrebbleBadge 
                          count={`${chip.hits} ${window.getI18n(ti18n, 'INSTANCES')}`}
                          color={getBadgeColor(chip.hits)}
                        />
                      </TrebbleSpace>
                      <Text type="secondary">
                        {chip.rationale}
                      </Text>
                    </TrebbleSpace>
                  </div>
                  <TrebbleSwitch
                    checked={chipStates[chip.chipLabel]}
                    onChange={(checked) => handleChipToggle(chip.chipLabel, checked)}
                  />
                </TrebbleSpace>
              </TrebbleCard>
            ))}
          </TrebbleSpace>
        </div>

        {/* Strategic Questions */}
        {plan.probingQuestions.length > 0 && (
          <div>
            <Title level={4}>{window.getI18n(ti18n, 'STRATEGIC_QUESTIONS')}</Title>
            <TrebbleSpace direction="vertical" size="middle" style={{ width: '100%' }}>
              {plan.probingQuestions.map((question) => (
                <TrebbleCard key={question.id}>
                  <TrebbleSpace direction="vertical" size="small" style={{ width: '100%' }}>
                    <Text strong>{question.text}</Text>
                    {question.questionType === 'multiple-choice' && question.options ? (
                      <TrebbleRadio.Group
                        value={questionAnswers[question.id] || ""}
                        onChange={(e) => handleQuestionAnswer(question.id, e.target.value)}
                        style={{ width: '100%' }}
                      >
                        <TrebbleSpace direction="vertical" style={{ width: '100%' }}>
                          {question.options.map((option, optionIndex) => (
                            <TrebbleRadio key={optionIndex} value={option} style={{ display: 'block', padding: '4px 0' }}>
                              {option}
                            </TrebbleRadio>
                          ))}
                          <TrebbleRadio value="custom" style={{ display: 'block', padding: '4px 0' }}>
                            {window.getI18n(ti18n, 'OTHER_PLEASE_SPECIFY')}
                            {questionAnswers[question.id] === "custom" && (
                              <TrebbleInput
                                placeholder={window.getI18n(ti18n, 'PLEASE_SPECIFY_PLACEHOLDER')}
                                onChange={(e) => handleQuestionAnswer(question.id, `custom: ${e.target.value}`)}
                                style={{ marginTop: 4, marginLeft: 8 }}
                              />
                            )}
                          </TrebbleRadio>
                        </TrebbleSpace>
                      </TrebbleRadio.Group>
                    ) : (
                      <TrebbleInput.TextArea
                        rows={2}
                        placeholder={window.getI18n(ti18n, 'YOUR_ANSWER_PLACEHOLDER')}
                        value={questionAnswers[question.id] || ""}
                        onChange={(e) => handleQuestionAnswer(question.id, e.target.value)}
                      />
                    )}
                  </TrebbleSpace>
                </TrebbleCard>
              ))}
            </TrebbleSpace>
          </div>
        )}

        {/* Additional Directives */}
        <div>
          <Title level={4}>{window.getI18n(ti18n, 'ADDITIONAL_DIRECTIVES')}</Title>
          <TrebbleInput.TextArea
            rows={3}
            placeholder={window.getI18n(ti18n, 'ADDITIONAL_DIRECTIVES_PLACEHOLDER')}
            value={additionalDirectives}
            onChange={(e) => setAdditionalDirectives(e.target.value)}
          />
        </div>
      </TrebbleSpace>
    </TrebbleModal>
  );
} 